import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Truck,
  Activity,
  Wrench,
  Gauge,
  AlertTriangle,
  Fuel,
  FileWarning,
  DollarSign,
  BarChart3,
  TrendingUp,
  MapPin,
  ClipboardList,
  Settings,
  ArrowRight,
} from 'lucide-react';

interface DashboardData {
  totalVehicles: number;
  activeVehicles: number;
  inMaintenance: number;
  avgMileage: number;
  overdueMaintenance: number;
  lowFuel: number;
  expiringDocuments: number;
  monthlyFuelCost: number;
  fleetUtilization: number;
  avgMilesPerVehicle: number;
  maintenanceSpend: number;
}

const EMPTY: DashboardData = {
  totalVehicles: 0,
  activeVehicles: 0,
  inMaintenance: 0,
  avgMileage: 0,
  overdueMaintenance: 0,
  lowFuel: 0,
  expiringDocuments: 0,
  monthlyFuelCost: 0,
  fleetUtilization: 0,
  avgMilesPerVehicle: 0,
  maintenanceSpend: 0,
};

const quickActions = [
  { label: 'Vehicles', icon: Truck, path: '/vehicles', desc: 'Manage fleet vehicles' },
  { label: 'GPS Tracking', icon: MapPin, path: '/gps', desc: 'Real-time tracking' },
  { label: 'Maintenance', icon: Wrench, path: '/maintenance', desc: 'Service schedules' },
  { label: 'Reports', icon: ClipboardList, path: '/reports', desc: 'Fleet analytics' },
  { label: 'Admin', icon: Settings, path: '/admin', desc: 'System settings' },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useApi<DashboardData>('/api/dashboard');
  const d = data ?? EMPTY;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fleet Management"
        description="Overview of your fleet operations"
        actions={isLoading ? <Spinner size={16} /> : undefined}
      />

      {error && (
        <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: '#EF4444' }} />
            <span className="text-sm" style={{ color: '#EF4444' }}>Unable to load fleet data. Please try again.</span>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Truck} label="Total Vehicles" value={d.totalVehicles} />
        <StatCard icon={Activity} label="Active" value={d.activeVehicles} accent="success" />
        <StatCard icon={Wrench} label="In Maintenance" value={d.inMaintenance} accent="warning" />
        <StatCard icon={Gauge} label="Avg Mileage" value={`${d.avgMileage.toLocaleString()} mi`} />
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <AlertCard icon={AlertTriangle} label="Overdue Maintenance" value={d.overdueMaintenance} variant="destructive" />
        <AlertCard icon={Fuel} label="Low Fuel" value={d.lowFuel} variant="warning" />
        <AlertCard icon={FileWarning} label="Expiring Documents" value={d.expiringDocuments} variant="warning" />
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Monthly Fuel Cost" value={`$${d.monthlyFuelCost.toLocaleString()}`} />
        <StatCard icon={BarChart3} label="Fleet Utilization" value={`${d.fleetUtilization}%`} />
        <StatCard icon={TrendingUp} label="Avg Miles/Vehicle" value={d.avgMilesPerVehicle.toLocaleString()} />
        <StatCard icon={Wrench} label="Maintenance Spend" value={`$${d.maintenanceSpend.toLocaleString()}`} />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--foreground)' }}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {quickActions.map((a) => (
            <button
              key={a.path}
              type="button"
              onClick={() => navigate(a.path)}
              className="group text-left rounded-xl p-4 transition-all duration-150"
              style={{
                background: 'var(--glass-bg)',
                border: '1px solid var(--glass-border)',
                backdropFilter: 'blur(var(--glass-blur))',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-border)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--glass-border)';
              }}
            >
              <a.icon className="w-5 h-5 mb-2" style={{ color: 'var(--accent)' }} />
              <div className="text-sm font-medium flex items-center gap-1" style={{ color: 'var(--foreground)' }}>
                {a.label}
                <ArrowRight className="w-3 h-3" style={{ color: 'var(--muted-foreground)' }} />
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                {a.desc}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AlertCard({
  icon: Icon,
  label,
  value,
  variant,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: number;
  variant: 'destructive' | 'warning';
}) {
  const color = variant === 'destructive' ? 'var(--destructive)' : 'var(--warning)';
  const bg = variant === 'destructive' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)';

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg" style={{ background: bg }}>
              <Icon className="w-4 h-4" style={{ color }} />
            </div>
            <span className="text-sm" style={{ color: 'var(--foreground)' }}>{label}</span>
          </div>
          <span className="text-2xl font-bold" style={{ color }}>{value}</span>
        </div>
      </CardContent>
    </Card>
  );
}
